/**
 * DefaultSOSTable Component
 *
 * Allows users to configure default SOS officers before generating the roster.
 * Each officer can be assigned to multiple counters at different time ranges.
 * Columns: Checked, Name, Deployment, Start Time, End Time, Counter No.
 */

import { Fragment, useState, useEffect } from "react";
import { NUM_SLOTS, generateTimeSlots, formatCounterLabel, getStartHour, hmmmToEndSlot, hmmmToSlot, isValidTimeRange } from "@/config/modeConfig";
import { apiClient } from "@/api/client";
import { BREAK_LABELS, BreakSelect } from "@/components/BreakSizeSelects";
import { HwCounterPicker } from "@/components/HwCounterPicker";
import { DurationPill } from "@/components/DurationPill";
import { WINDOW_COLORS } from "@/utils/windowDuration";
import type { CounterVacancy, ExtraOccupant } from "@/utils/hwCounterVacancy";
import { slotToHhmm } from "@/utils/partialTiming";
import { applySupportOfficers, type SupportOfficerInfo } from "@/utils/supportOfficers";
import { buildDefaultOfficers, generateCounterOptions, uid } from "@/utils/defaultSosOfficers";

const PINK = "#e879a0";          // the SOS accent, as on the Officers tab's SOS card
const PINK_BG = "#fdf2f8";
const PINK_HEADER = "#f9a8c9";    // a darker pink band: where one officer's card starts
const PINK_TEXT = "#9d174d";

/** A text field that wraps and grows to fit what is typed, instead of scrolling along one line. */
function WrapField({ value, onChange, placeholder, style, label }: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  style: React.CSSProperties;
  label: string;
}) {
  return (
    <textarea
      rows={1}
      value={value}
      placeholder={placeholder}
      aria-label={label}
      ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }}
      onChange={(e) => onChange(e.target.value.replace(/\n/g, " "))}
      style={{ ...style, resize: "none", overflow: "hidden", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.3, fontFamily: "inherit", minHeight: 28 }}
    />
  );
}

export interface SOSCounterRow {
  id: string; // unique row id
  startTime: string;
  endTime: string;
  counter: string;
  /** This window's break plan in 15-minute slots, used only when `breakModified` (the PDF printed one, or the
   *  user set it); otherwise the default for the window's timing is shown. Only meaningful while the window
   *  is Auto — a counter chosen for it needs no plan of its own. */
  breakSizes?: number[];
  breakModified?: boolean;
  /** The counter this position normally opens on, when the PDF moved the officer
   *  off it. Set only on a replaced row, and only to flag the change in the UI. */
  replacedFrom?: string;
}

export interface SOSOfficer {
  officerId: string; // unique officer id
  name: string;
  deployment: string;
  checked: boolean;
  counterRows: SOSCounterRow[];
  /** The support-page S/N this row was seeded from, once resolved from a
   *  placeholder ("SN2") to a real name — kept so a later PDF can still find
   *  and re-sync this row even though its name no longer reads "SN2". A row
   *  named by hand, never a placeholder, never gets one and is left alone. */
  supportSn?: number;
  /** "fixed" (the default): every window's counter is set and is used exactly as shown. "first": the roster
   *  plans the officer — a window with a counter opens on it, a blank one (Auto) is the roster's to choose and
   *  carries its own breaks. Choosing Auto on a "fixed" officer's window makes them "first". See utils/sosRows.ts. */
  kind?: "fixed" | "first";
  /** A short note beside the name, e.g. "Waiting for chops". */
  note?: string;
}

/** Minutes from a window's start to its end, 0 while either is missing or they are out of order. */
function windowMinutes(start: string, end: string, shiftType: "morning" | "night"): number {
  try {
    const startHour = getStartHour(shiftType);
    return Math.max(0, hmmmToEndSlot(end, startHour) - hmmmToSlot(start, startHour)) * 15;
  } catch { return 0; }
}

interface DefaultSOSTableProps {
  shiftType: "morning" | "night";
  operationMode: "arrival" | "departure";
  /** Preserved officer state from the parent — restores edits after remount. */
  initialOfficers?: SOSOfficer[];
  /** Error keys from parent validation. Format: "<officerId>:deployment" or "<officerId>:<rowId>:<field>". */
  validationErrors?: Set<string>;
  /** Rows of the support page read off the uploaded PDF, if there was one. */
  supportOfficers?: SupportOfficerInfo[];
  /** Who holds each counter at a slot and until when — the same picker step 3 shows. `extras` are the other SOS
   *  officers' chosen counters, which take counters too. */
  vacancyAt?: (slot: number, extras: ExtraOccupant[]) => Map<number, CounterVacancy>;
  /** Whether a printed roster backs `vacancyAt`. */
  hasRoster?: boolean;
  onChange?: (officers: SOSOfficer[]) => void;
}

/** True on a phone-width screen, where the wide table becomes a stack of cards instead of scrolling sideways. */
function useNarrow(): boolean {
  const query = "(max-width: 700px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches === true);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

export function DefaultSOSTable({
  shiftType,
  operationMode,
  initialOfficers,
  validationErrors,
  supportOfficers = [],
  vacancyAt,
  hasRoster = false,
  onChange,
}: DefaultSOSTableProps) {
  const narrow = useNarrow();
  const timeSlots = generateTimeSlots(shiftType);
  const counterOptions = generateCounterOptions(operationMode);

  // Use parent-preserved state on remount (survives Next→Back navigation).
  // Falls back to buildDefaultOfficers() on first render.
  const [officers, setOfficers] = useState<SOSOfficer[]>(() =>
    applySupportOfficers(
      initialOfficers && initialOfficers.length > 0
        ? initialOfficers
        : buildDefaultOfficers(shiftType, operationMode),
      supportOfficers,
    ),
  );

  // Sync initial officers to parent on mount so the parent's sosOfficers state
  // is populated even if the user never interacts with this table.
  useEffect(() => {
    onChange?.(officers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notify = (updated: SOSOfficer[]) => {
    setOfficers(updated);
    onChange?.(updated);
  };

  // The default break plan for each window the roster picks a counter for, as Add SOS gets it from the backend.
  const [defaultBreaks, setDefaultBreaks] = useState<Record<string, (number | null)[]>>({});
  /** A window with no counter chosen: the roster picks its counter. */
  const isAutoRow = (row: SOSCounterRow) => !row.counter;
  // Every window carries its own breaks, a counter chosen for it or not: the counter is only where it opens.
  const allRows = officers.flatMap((o) => o.counterRows);
  const timingKey = allRows.map((r) => `${r.id}:${r.startTime}-${r.endTime}`).join("|");
  useEffect(() => {
    let cancelled = false;
    for (const r of allRows) {
      if (!r.startTime || !r.endTime) continue;
      apiClient.getBreakPattern({ timing: `${r.startTime}-${r.endTime}`, officer_type: "sos", shift: shiftType })
        .then(({ pattern }) => {
          if (!cancelled) setDefaultBreaks((prev) => ({ ...prev, [r.id]: [pattern[0] ?? null, pattern[1] ?? null, pattern[2] ?? null] }));
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timingKey, shiftType]);

  const breaksOf = (row: SOSCounterRow): (number | null)[] =>
    row.breakModified ? [row.breakSizes?.[0] ?? null, row.breakSizes?.[1] ?? null, row.breakSizes?.[2] ?? null]
      : defaultBreaks[row.id] ?? [null, null, null];
  const mapRow = (officerId: string, rowId: string, change: (r: SOSCounterRow) => SOSCounterRow) =>
    notify(officers.map((o) => (o.officerId !== officerId ? o
      : { ...o, counterRows: o.counterRows.map((r) => (r.id === rowId ? change(r) : r)) })));
  const setBreak = (officerId: string, rowId: string, index: number, value: number | null) =>
    mapRow(officerId, rowId, (r) => {
      const next = breaksOf(r);
      next[index] = value;
      return { ...r, breakModified: true, breakSizes: next.filter((x): x is number => x !== null) };
    });
  const resetBreaks = (officerId: string, rowId: string) =>
    mapRow(officerId, rowId, (r) => ({ ...r, breakModified: false, breakSizes: [] }));

  /** Other SOS officers' chosen counters — they hold those counters then, so the picker does not call them free. */
  const extrasFor = (officerId: string): ExtraOccupant[] =>
    officers.filter((o) => o.officerId !== officerId && o.checked).flatMap((o) =>
      o.counterRows.filter((r) => r.counter && r.startTime && r.endTime).map((r) => {
        const slots = new Array<number>(NUM_SLOTS).fill(0);
        const from = hmmmToSlot(r.startTime, getStartHour(shiftType));
        const to = Math.min(hmmmToEndSlot(r.endTime, getStartHour(shiftType)), NUM_SLOTS);
        for (let i = Math.max(from, 0); i < to; i++) slots[i] = Number(r.counter);
        return { name: o.name, slots };
      }));

  // --- Officer-level updates ---

  const toggleChecked = (officerId: string) => {
    notify(
      officers.map((o) =>
        o.officerId === officerId ? { ...o, checked: !o.checked } : o,
      ),
    );
  };

  const updateOfficerField = (
    officerId: string,
    field: "name" | "deployment",
    value: string,
  ) => {
    notify(
      officers.map((o) =>
        o.officerId === officerId ? { ...o, [field]: value } : o,
      ),
    );
  };

  // --- Counter-row-level updates ---

  const updateCounterRow = (
    officerId: string,
    rowId: string,
    field: keyof Omit<SOSCounterRow, "id">,
    value: string,
  ) => {
    notify(
      officers.map((o) =>
        o.officerId === officerId
          ? {
              ...o,
              // Auto on a window of an officer whose counters were all set hands the planning to the roster.
              ...(field === "counter" && value === "" ? { kind: "first" as const } : {}),
              counterRows: o.counterRows.map((r) =>
                r.id === rowId ? { ...r, [field]: value } : r,
              ),
            }
          : o,
      ),
    );
  };

  const addCounterRowAfter = (officerId: string, afterRowId: string) => {
    notify(
      officers.map((o) => {
        if (o.officerId !== officerId) return o;
        const idx = o.counterRows.findIndex((r) => r.id === afterRowId);
        const afterRow = o.counterRows[idx];
        const newRow: SOSCounterRow = {
          id: uid(),
          startTime: afterRow ? afterRow.endTime : timeSlots[0],
          endTime: timeSlots[timeSlots.length - 1],
          // A new window starts as Auto: the roster picks its counter. Pick one to pin it.
          counter: "",
        };
        const newRows = [...o.counterRows];
        newRows.splice(idx + 1, 0, newRow);
        return { ...o, kind: "first" as const, counterRows: newRows };
      }),
    );
  };

  const removeCounterRow = (officerId: string, rowId: string) => {
    notify(
      officers.map((o) =>
        o.officerId === officerId
          ? {
              ...o,
              counterRows: o.counterRows.filter((r) => r.id !== rowId),
            }
          : o,
      ),
    );
  };

  // --- Officer-level add/remove ---

  const addOfficer = () => {
    const newOfficer: SOSOfficer = {
      officerId: uid(),
      name: "",
      deployment: "",
      checked: true,
      counterRows: [
        {
          id: uid(),
          startTime: timeSlots[0],
          endTime: timeSlots[timeSlots.length - 1],
          counter: counterOptions[0],
        },
      ],
    };
    notify([...officers, newOfficer]);
  };

  const removeOfficer = (officerId: string) => {
    notify(officers.filter((o) => o.officerId !== officerId));
  };

  // Helper: check if a field has a validation error
  const hasError = (key: string) => validationErrors?.has(key) ?? false;

  // Styles
  const cellBase: React.CSSProperties = {
    padding: "4px 6px",
    fontSize: "13px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    background: "white",
    color: "#333",
  };

  const inputStyle = (errorKey: string): React.CSSProperties => ({
    ...cellBase,
    width: "100%",
    boxSizing: "border-box",
    ...(hasError(errorKey) ? { borderColor: "#ef4444", color: "#ef4444" } : {}),
  });

  const selectStyle = (errorKey: string, extraInvalid = false): React.CSSProperties => ({
    ...cellBase,
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    ...((hasError(errorKey) || extraInvalid) ? { borderColor: "#ef4444", color: "#ef4444" } : {}),
  });

  const headerCellStyle: React.CSSProperties = {
    textAlign: "center",
    fontWeight: 700,
    fontSize: "12px",
    padding: "6px 4px",
    background: "#dbeafe",
    whiteSpace: "nowrap",
  };

  const iconBtnStyle = (color: string): React.CSSProperties => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    color,
    padding: "2px 4px",
    lineHeight: 1,
  });

  // Column widths (px). Total ≈ 640px fits with overflow-x.
  const COL = {
    check: 36,
    name: 130,
    deployment: 110,
    startTime: 90,
    endTime: 90,
    counter: 80,
    breaks: 66,
    actions: 70,
  };

  const gridTemplate = `${COL.check}px ${COL.name}px ${COL.deployment}px ${COL.startTime}px ${COL.endTime}px ${COL.counter}px repeat(3, ${COL.breaks}px) ${COL.actions}px`;
  // Width of columns 1-7 merged (used for the Add Officer row)
  const addOfficerWidth = COL.check + COL.name + COL.deployment + COL.startTime + COL.endTime + COL.counter + 3 * COL.breaks + 24; // +24 for 8 gaps of 3px
  const tableMinWidth = narrow ? 0 : addOfficerWidth + COL.actions + 3;

  // Two kinds of row, shown as two groups: every counter set for the whole shift, or only the first
  // window's counter written down (the roster plans the rest). One heading only when both are present.
  const fixedRows = officers.filter((o) => o.kind !== "first");
  const firstRows = officers.filter((o) => o.kind === "first");
  const sections = [
    { key: "fixed", heading: firstRows.length > 0 ? "Counters set for the whole shift" : null, rows: fixedRows },
    {
      key: "first",
      heading: "Pick a counter for a window, or leave it Auto for the roster to choose",
      rows: firstRows,
    },
  ].filter((section) => section.rows.length > 0);
  let displayIndex = 0;

  /** The counter of a window: every counter, coloured by whether it is free from the window's start and until
   *  when, with "Auto" first — the roster picks the counter (and plans the window's breaks). */
  const counterField = (officer: SOSOfficer, row: SOSCounterRow) => {
    const startSlot = (() => { try { return hmmmToSlot(row.startTime, getStartHour(shiftType)); } catch { return 0; } })();
    const vacancies = vacancyAt?.(startSlot, extrasFor(officer.officerId));
    return (
      <HwCounterPicker
        value={row.counter}
        onChange={(v) => updateCounterRow(officer.officerId, row.id, "counter", v)}
        options={[
          { value: "", label: "Auto" },
          ...counterOptions.map((c) => ({
            value: c,
            label: formatCounterLabel(parseInt(c), operationMode),
            vacancy: vacancies?.get(parseInt(c)),
          })),
        ]}
        slotTime={(slot) => slotToHhmm(slot, getStartHour(shiftType))}
        neededSlots={row.endTime ? hmmmToEndSlot(row.endTime, getStartHour(shiftType)) : 0}
        hasRoster={hasRoster}
        title={`Counter from ${row.startTime} — ${officer.name || officer.deployment || "SOS officer"}`}
        testId={`sos-counter-${officer.officerId}-${row.id}`}
        emphasis
      />
    );
  };

  /** The three break dropdowns of one window. A break left on its placeholder is the roster's to plan ("1st (auto)");
   *  once the window's plan is edited, a cleared break means none. A window with a counter chosen gets the
   *  lighter border, so the counter stays the heaviest thing on the row. */
  const breakFields = (officer: SOSOfficer, row: SOSCounterRow) =>
    BREAK_LABELS.map((bl, bIdx) => (
      <BreakSelect
        key={bl}
        strong={isAutoRow(row)}
        emptyLabel={`${bIdx + 1}${["st", "nd", "rd"][bIdx]} (${row.breakModified ? "none" : "auto"})`}
        label={`${bl} — ${officer.name || officer.deployment || "SOS officer"} ${row.startTime}-${row.endTime}`}
        value={breaksOf(row)[bIdx] ?? null}
        onChange={(v) => setBreak(officer.officerId, row.id, bIdx, v)}
      />
    ));

  // ── Phone layout: one pink card per officer, laid out like the Officers tab's SOS card ──
  const renderNarrowCard = (officer: SOSOfficer) => {
    const cardSelect = (errorKey: string, invalid: boolean): React.CSSProperties => ({
      width: "100%", boxSizing: "border-box", minWidth: 0, padding: "3px 2px", fontSize: 13, fontWeight: 700,
      background: "#fff", color: invalid || hasError(errorKey) ? "#ef4444" : "#000", borderRadius: 4, cursor: "pointer",
      border: `2px solid ${invalid || hasError(errorKey) ? "#ef4444" : "#334155"}`,
    });
    const field: React.CSSProperties = { ...inputStyle(""), padding: "3px 5px", fontSize: 13, background: "#fff" };
    return (
      <div
        key={officer.officerId}
        style={{ border: `3px solid ${PINK}`, borderRadius: 10, margin: "8px 0", background: "#fff", opacity: officer.checked ? 1 : 0.6, overflow: "hidden", boxShadow: "0 2px 5px rgba(157,23,77,0.15)" }}
      >
        {/* Header band: include tick, SOS pill, name, remove */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "5px 6px", background: PINK_HEADER, borderBottom: `2px solid ${PINK}` }}>
          <input
            type="checkbox"
            checked={officer.checked}
            aria-label="Include this officer"
            onChange={() => toggleChecked(officer.officerId)}
            style={{ width: 18, height: 18, marginTop: 5, cursor: "pointer", accentColor: "#6366f1", flexShrink: 0 }}
          />
          <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 7px", borderRadius: 99, background: "#fff", color: PINK_TEXT, marginTop: 4, flexShrink: 0 }}>SOS</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <WrapField
              value={officer.name} placeholder="Officer name (optional)" label="Officer name"
              onChange={(v) => updateOfficerField(officer.officerId, "name", v)} style={field}
            />
          </div>
          <button title="Remove officer" onClick={() => removeOfficer(officer.officerId)} style={{ ...iconBtnStyle("#be123c"), marginTop: 3 }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block" }}>
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
            </svg>
          </button>
        </div>

        <div style={{ padding: "5px 6px 6px" }}>
          {officer.note && (
            <div style={{ fontSize: 11, color: "#92400e", marginBottom: 5, lineHeight: 1.3 }}>{officer.note}</div>
          )}
          {/* Deployment, on one line with its label */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 5 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: PINK, letterSpacing: "0.05em", width: 40, flexShrink: 0, paddingTop: 8 }}>FROM *</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <WrapField
                value={officer.deployment} placeholder="Deployment" label="Deployment"
                onChange={(v) => updateOfficerField(officer.officerId, "deployment", v)} style={{ ...field, ...(hasError(`${officer.officerId}:deployment`) ? { borderColor: "#ef4444" } : {}) }}
              />
            </div>
          </div>

          {/* One compact box per availability window: times, duration, counter — and breaks when the roster picks the counter */}
          {officer.counterRows.map((row) => {
            const invalid = !isValidTimeRange(row.startTime, row.endTime, shiftType);
            const mins = windowMinutes(row.startTime, row.endTime, shiftType);
            return (
              <div key={row.id} style={{ border: `1.5px solid ${PINK}`, borderRadius: 7, marginBottom: 4, background: PINK_BG, padding: "4px 5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 62, flexShrink: 0 }}>
                    <select
                      aria-label="Start time" value={row.startTime}
                      onChange={(e) => updateCounterRow(officer.officerId, row.id, "startTime", e.target.value)}
                      style={cardSelect(`${officer.officerId}:${row.id}:startTime`, invalid)}
                    >
                      {timeSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <span style={{ color: PINK, fontSize: 11 }}>–</span>
                  <div style={{ width: 62, flexShrink: 0 }}>
                    <select
                      aria-label="End time" value={row.endTime}
                      onChange={(e) => updateCounterRow(officer.officerId, row.id, "endTime", e.target.value)}
                      style={cardSelect(`${officer.officerId}:${row.id}:endTime`, invalid)}
                    >
                      {!row.endTime && <option value="" disabled>HHMM</option>}
                      {timeSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <DurationPill minutes={mins} color={WINDOW_COLORS.SOS} />
                  <div style={{ flex: 1, minWidth: 64 }}>{counterField(officer, row)}</div>
                  {officer.counterRows.length > 1 && (
                    <button
                      title="Remove this availability window"
                      onClick={() => removeCounterRow(officer.officerId, row.id)}
                      style={{ ...iconBtnStyle(PINK_TEXT), fontSize: 14, padding: "0 2px" }}
                    >✕</button>
                  )}
                </div>
                {row.replacedFrom && (
                  <div style={{ fontSize: 10, color: "#92400e", paddingTop: 2 }}>was {formatCounterLabel(parseInt(row.replacedFrom), operationMode)}</div>
                )}
                {/* This window's own breaks, inside it — whether or not a counter is chosen for it. */}
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, paddingTop: 4, borderTop: `1px dashed ${PINK}` }}>
                    <span
                      title="Breaks the roster plans within this window"
                      style={{ fontSize: 9, fontWeight: 700, color: PINK_TEXT, letterSpacing: "0.04em", flexShrink: 0, width: 36, lineHeight: 1.1 }}
                    >BREAKS</span>
                    {breakFields(officer, row).map((el, i) => <div key={i} style={{ flex: 1, minWidth: 0 }}>{el}</div>)}
                    {row.breakModified && (
                      <button title="Reset this window's breaks to the defaults" onClick={() => resetBreaks(officer.officerId, row.id)} style={{ ...iconBtnStyle(PINK_TEXT), padding: "0 2px" }}>↺</button>
                    )}
                </div>
              </div>
            );
          })}

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => addCounterRowAfter(officer.officerId, officer.counterRows[officer.counterRows.length - 1].id)}
              style={{ fontSize: 11, padding: "3px 8px", background: "#fff", color: PINK_TEXT, border: `1.5px dashed ${PINK}`, borderRadius: 5, cursor: "pointer", fontWeight: 700 }}
            >
              + window
            </button>
            {officer.kind !== "first" && <span style={{ fontSize: 10, color: "#94a3b8" }}>as printed — pick Auto on a window to let the roster plan it</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* Section header — no button here, button is at bottom of table */}
      <div style={{ marginBottom: "8px" }}>
        <label style={{ fontWeight: 600 }}>SOS officers</label>
      </div>

      <p style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
        Pre-assign SOS officers to specific counters and time windows. Tick an officer to include them;
        leave one unticked to skip them.
      </p>

      <div style={{ overflowX: "auto" }}>
        {/* Table Header */}
        <div
          style={{
            display: narrow ? "none" : "grid",
            gridTemplateColumns: gridTemplate,
            gap: "3px",
            borderRadius: "6px 6px 0 0",
            marginBottom: "2px",
            minWidth: tableMinWidth,
          }}
        >
          <div style={headerCellStyle}>✓</div>
          <div style={headerCellStyle}>Name</div>
          <div style={headerCellStyle}>Deployment *</div>
          <div style={headerCellStyle}>Start Time *</div>
          <div style={headerCellStyle}>End Time *</div>
          <div style={headerCellStyle}>Counter No. *</div>
          {BREAK_LABELS.map((label) => (
            <div key={label} style={headerCellStyle}>{label}</div>
          ))}
          <div style={headerCellStyle}></div>
        </div>

        {/* Officer rows, grouped */}
        {sections.map((section) => (
          <Fragment key={section.key}>
            {section.heading && (
              <div
                style={{
                  fontSize: "11px", fontWeight: 700, color: "#475569", background: "#f1f5f9",
                  padding: "4px 8px", margin: "6px 0 2px", borderRadius: "4px", minWidth: tableMinWidth,
                }}
              >
                {section.heading}
              </div>
            )}
        {section.rows.map((officer) => {
          if (narrow) return renderNarrowCard(officer);
          const oIdx = displayIndex++;
          const rowBg = oIdx % 2 === 0 ? "white" : "#f8f9fa";
          const numRows = officer.counterRows.length;

          return (
            <div
              key={officer.officerId}
              style={{
                background: rowBg,
                borderBottom: "1px solid #e0e0e0",
                minWidth: tableMinWidth,
                opacity: officer.checked ? 1 : 0.6,
              }}
            >
              {officer.counterRows.map((row, rIdx) => {
                const isFirst = rIdx === 0;

                return (
                  <div
                    key={row.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: gridTemplate,
                      gap: "3px",
                      padding: "3px 0",
                      alignItems: "start",
                    }}
                  >
                    {/* Col 1: Checkbox — only on first sub-row */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        visibility: isFirst ? "visible" : "hidden",
                        height: "100%",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={officer.checked}
                        onChange={() => toggleChecked(officer.officerId)}
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                          accentColor: "#6366f1",
                        }}
                      />
                    </div>

                    {/* Col 2: Name — only on the first sub-row (empty, not hidden, below: a hidden field keeps its height) */}
                    <div>
                      {isFirst && (
                        <>
                          <WrapField
                            value={officer.name}
                            placeholder="Officer name (optional)"
                            label="Officer name"
                            onChange={(v) => updateOfficerField(officer.officerId, "name", v)}
                            style={inputStyle("")}
                          />
                          {officer.note && (
                            <div style={{ fontSize: 10, color: "#92400e", marginTop: 2, lineHeight: 1.2 }}>{officer.note}</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Col 3: Deployment — only on the first sub-row */}
                    <div>
                      {isFirst && (
                        <WrapField
                          value={officer.deployment}
                          placeholder="Deployment"
                          label="Deployment"
                          onChange={(v) => updateOfficerField(officer.officerId, "deployment", v)}
                          style={inputStyle(`${officer.officerId}:deployment`)}
                        />
                      )}
                    </div>

                    {/* Col 4: Start Time */}
                    <div>
                      <select
                        value={row.startTime}
                        onChange={(e) =>
                          updateCounterRow(
                            officer.officerId,
                            row.id,
                            "startTime",
                            e.target.value,
                          )
                        }
                        style={selectStyle(
                          `${officer.officerId}:${row.id}:startTime`,
                          !isValidTimeRange(row.startTime, row.endTime, shiftType),
                        )}
                      >
                        {timeSlots.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Col 5: End Time */}
                    <div>
                      <select
                        value={row.endTime}
                        onChange={(e) =>
                          updateCounterRow(
                            officer.officerId,
                            row.id,
                            "endTime",
                            e.target.value,
                          )
                        }
                        style={selectStyle(
                          `${officer.officerId}:${row.id}:endTime`,
                          !isValidTimeRange(row.startTime, row.endTime, shiftType),
                        )}
                      >
                        {!row.endTime && <option value="" disabled>HHMM</option>}
                        {timeSlots.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <div style={{ display: "flex", justifyContent: "center", marginTop: 3 }}>
                        <DurationPill
                          minutes={windowMinutes(row.startTime, row.endTime, shiftType)}
                          color={WINDOW_COLORS.SOS}
                        />
                      </div>
                    </div>

                    {/* Col 6: Counter No. — flagged when the PDF moved this
                        officer off the counter their position normally opens on. A handwritten
                        officer has a counter for the first window only; the roster plans the rest. */}
                    <div>
                      {counterField(officer, row)}
                      {row.replacedFrom && (
                        <div style={{ fontSize: 9, color: "#92400e", marginTop: 2, textAlign: "center" }}>
                          was {formatCounterLabel(parseInt(row.replacedFrom), operationMode)}
                        </div>
                      )}
                    </div>

                    {/* Cols 7-9: this window's breaks — a counter chosen for it is only where it opens. */}
                    {breakFields(officer, row).map((el, i) => <div key={i}>{el}</div>)}

                    {/* Col 10: Row actions — both buttons on every row */}
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        paddingTop: 2,
                      }}
                    >
                      {row.breakModified && (
                        <button
                          title="Reset this window's breaks to the defaults"
                          onClick={() => resetBreaks(officer.officerId, row.id)}
                          style={iconBtnStyle("#6366f1")}
                        >
                          ↺
                        </button>
                      )}
                      {/* Add counter row after this row */}
                      <button
                        title="Add counter row after this"
                        onClick={() => addCounterRowAfter(officer.officerId, row.id)}
                        style={iconBtnStyle("#6366f1")}
                      >
                        +
                      </button>

                      {/* Remove this counter row (if >1 row) OR remove officer (if only row) */}
                      <button
                        title={
                          numRows === 1
                            ? "Remove officer"
                            : "Remove this counter row"
                        }
                        onClick={() => {
                          if (numRows === 1) {
                            removeOfficer(officer.officerId);
                          } else {
                            removeCounterRow(officer.officerId, row.id);
                          }
                        }}
                        style={iconBtnStyle("#ef4444")}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="13"
                          height="13"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ display: "block" }}
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Divider between officers */}
              <div
                style={{
                  borderTop: "1px dashed #e2e8f0",
                  margin: "2px 0 0",
                  height: "1px",
                }}
              />
            </div>
          );
        })}
          </Fragment>
        ))}

        {/* Add Officer — full-width row spanning cols 1-6, distinct from per-officer "+" */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: narrow ? "1fr" : `${addOfficerWidth}px ${COL.actions}px`,
            gap: "3px",
            minWidth: tableMinWidth,
            padding: "4px 0",
          }}
        >
          <button
            onClick={addOfficer}
            style={{
              padding: "6px",
              borderRadius: "6px",
              border: "1px dashed #6366f1",
              background: "#f5f3ff",
              color: "#6366f1",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
              width: "100%",
            }}
          >
            + Add Officer
          </button>
          <div />
        </div>
      </div>

      {/* Legend */}
      <p style={{ fontSize: "11px", color: "#888", marginTop: "6px" }}>
        *Compulsory fields
      </p>
    </div>
  );
}

